import React, { useCallback, useMemo } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';

import { Checkbox, mergeClasses } from '@fluentui/react-components';

import {
  // ClarityTags,
  // useClarityTags,
  sizeValidForRatio,
  getPlacementConfig,
  AdsStudioSpinner,
  getPublisherTypeConfig
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
// import { Alert } from '@bingads-webui-react/alert';
// import { HelpButton } from '@bingads-webui-react/help-popup';
// import { Glyphicon } from '@bingads-webui-react/primitive';
// import { notificationApi } from '@bingads-webui-campaign/notification-center';

import {
  AdPreviewGridLayout,
  AdPreviewWaterfallGridLayout,
} from '@bingads-webui-campaign-react/unified-ad-preview-library-cmui';

import { PlacementContainer, PlacementContainerV2 } from './placement-container';
import { AdTypeControl } from './ad-type-control';
import { PublisherTypeControl } from './publisher-type-control';
import { usePlacementListStyles } from './styles';

// import { PlacementEditingModal } from './placement-editing-modal';
// import { AssetLibraryPlacementContainer } from './placement-container';
// import { ImageCropPanel } from './image-crop-panel';
// import { UpdateConfirmDialog } from './update-confim-dialog';
// import { getPageStyles } from './styles';

export const AdPlacementListView = ({
  placementList,
  firstImage,
  onEditPlacement,
  adType,
  showRequiredOnly,
  previewTexts,
  i18n,
  switchAdType,
  switchShowRequiredOnly,
  fixedAdType,
}) => {
  const classes = usePlacementListStyles();
  // render a placement
  const onRenderPlacement = useCallback((placement, AdPlacementComponent) => {
    const { placementMeta } = placement || {};
    const { imageMeta, imageCropping } = firstImage || {};

    if (!placementMeta || !imageMeta) {
      return null;
    }

    const placementConfigList = getPlacementConfig(adType);
    const placementConfig = placementConfigList[placementMeta.placementType];

    // hide non-required placements if showRequiredOnly is true
    if (showRequiredOnly && !placementConfig.isRequired) {
      return null;
    }

    const isValid = sizeValidForRatio(placementMeta.minSize, imageCropping || {
      SourceX: 0, SourceY: 0, SourceWidth: imageMeta.originalWidth, SourceHeight: imageMeta.originalHeight,
    });

    return (
      <PlacementContainer
        {...placementConfig}
        minSize={placementMeta.minSize}
        isValid={isValid}
        onCrop={() => onEditPlacement({ placement, ...placementConfig })}
        i18n={i18n}
        key={placementMeta.placementType}
      >
        <AdPlacementComponent
          isEnabled={isValid}
          placementData={placement}
          {...previewTexts}
          i18n={i18n}
        />
      </PlacementContainer>
    );
  }, [adType, firstImage, i18n, onEditPlacement, previewTexts, showRequiredOnly]);

  return (
    placementList && placementList.length > 0 ?
      <div className={mergeClasses(mergeClasses('placement-list-view', classes.placementListView, 'ad-placement-list-view'))}>
        <div className={mergeClasses(classes.header, 'header')}>
          {fixedAdType !== true && <AdTypeControl adType={adType} onChange={switchAdType} i18n={i18n} />}
          <Checkbox
            label={i18n.getString(_TL_('Show required placements only'))}
            onChange={(event, data) => { switchShowRequiredOnly(data.checked); }}
            className={classes.showRequiredOnlyCheckbox}
          />
        </div>
        <div className={mergeClasses(classes.placementListWrapper, 'placement-list-wrapper')} aria-live="polite">
          <AdPreviewGridLayout
            placementList={placementList}
            onRenderPlacement={onRenderPlacement}
            onRenderEmptyView={() => (
              <AdsStudioSpinner useCustomSize customStyle={{ top: '-100px', position: 'relative', paddingTop: '200px' }} />
            )}
            gridLayoutContainerClassName={classes.adPreviewGridLayoutContainer}
          />
        </div>
      </div> : null
      //   {
      //     showUIBlocker &&
      //     <Overlay className={classes.overlay}>
      //       <AssetLibrarySpinner useCustomSize />
      //     </Overlay>
      //   }
      // </div> : null
  );
};

AdPlacementListView.propTypes = {
  placementList: PropTypes.arrayOf(PropTypes.shape({})),
  firstImage: PropTypes.shape({}),
  imageInfo: PropTypes.shape({}),
  onEditPlacement: PropTypes.func.isRequired,
  adType: PropTypes.string.isRequired,
  showRequiredOnly: PropTypes.bool.isRequired,
  previewTexts: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({}).isRequired,
};

AdPlacementListView.defaultProps = {
  firstImage: null,
  imageInfo: null,
  placementList: [],
};


export const SizeListView = ({
  placementList,
  fixedAdType,
  i18n,
  adType,
  switchAdType,
  switchShowRequiredOnly,
  publisher,
  firstImage,
  onEditPlacement,
  showRequiredOnly,
  previewTexts,
  switchPublisher,
  permissions,
}) => {
  const classes = usePlacementListStyles();
  const isPMaxAspectRatiosV2Enabled = useMemo(() => permissions && permissions.dynamic.PMaxNewAspectRatios && !permissions.dynamic.PMaxExcludeNewAspectRatios, [permissions]);

  const publisherTypeList = useMemo(() => {
    const publisherTypeConfig = getPublisherTypeConfig({ isPMaxAspectRatiosV2Enabled });
    return publisherTypeConfig[adType];
  }, [adType, isPMaxAspectRatiosV2Enabled]);

  // render a placement
  const onRenderPlacement = useCallback((placement, AdPlacementComponent) => {
    const { placementMeta } = placement || {};
    const { imageMeta, imageCropping } = firstImage || {};

    if (!placementMeta || !imageMeta) {
      return null;
    }

    const placementConfigList = getPlacementConfig(adType);
    const placementConfig = placementConfigList[placementMeta.placementType];

    // hide non-required placements if showRequiredOnly is true
    if (showRequiredOnly && !placementConfig.isRequired) {
      return null;
    }

    if (publisher) {
      if (_.isArray(placementConfig.publisher)) {
        if (!placementConfig.publisher.includes(publisher)) {
          return null;
        }
      } else if (placementConfig.publisher !== publisher) {
        return null;
      }
    }

    const isValid = sizeValidForRatio(placementMeta.minSize, imageCropping || {
      SourceX: 0, SourceY: 0, SourceWidth: imageMeta.originalWidth, SourceHeight: imageMeta.originalHeight,
    });

    return (
      <PlacementContainerV2
        {...placementConfig}
        minSize={placementMeta.minSize}
        isValid={isValid}
        onCrop={() => onEditPlacement({ placement, ...placementConfig })}
        i18n={i18n}
        key={placementMeta.placementType}
      >
        <AdPlacementComponent
          isEnabled={isValid}
          placementData={placement}
          {...previewTexts}
          i18n={i18n}
        />
      </PlacementContainerV2>
    );
  }, [adType, firstImage, i18n, onEditPlacement, previewTexts, publisher, showRequiredOnly]);

  return (
    placementList && placementList.length > 0 ?
      <div className={mergeClasses(mergeClasses('placement-list-view', classes.placementListView, 'ad-placement-list-view'))}>
        <div className={mergeClasses(classes.header, 'header')}>
          {fixedAdType !== true && <AdTypeControl adType={adType} onChange={switchAdType} i18n={i18n} />}
          <PublisherTypeControl i18n={i18n} publisherTypeList={publisherTypeList} fixedAdType={fixedAdType} switchPublisher={switchPublisher} publisher={publisher} />
          <Checkbox
            label={i18n.getString(_TL_('Show required placements only'))}
            onChange={(event, data) => { switchShowRequiredOnly(data.checked); }}
            className={classes.showRequiredOnlyCheckbox}
          />
        </div>
        <div className={mergeClasses(classes.placementListWrapper, 'placement-list-wrapper')} aria-live="polite">
          <AdPreviewWaterfallGridLayout
            cardMinWidth={540}
            columnGap={24}
            rowGap={24}
            placementList={placementList}
            onRenderPlacement={onRenderPlacement}
            onRenderEmptyView={() => (
              <AdsStudioSpinner useCustomSize customStyle={{ top: '-100px', position: 'relative', paddingTop: '200px' }} />
            )}
            gridLayoutContainerClassName={classes.adPreviewWaterfallGridLayoutContainer}
          />
        </div>
      </div> : null
  );
};
